<template>
    <div
        class="modal-content-root d-flex flex-column overflow-hidden"
        :class="{
            'modal-content-root--flat-chrome': flatChrome || stitch,
            'modal-content-root--stitch': stitch,
            'modal-content-root--fill': fillHeight,
            'modal-content-root--immersive': immersive,
        }"
    >
        <v-btn v-if="immersive" icon class="modal-immersive-close" :disabled="closeDisabled" :aria-label="$translate('immersiveClose')" @click="$emit('close')">
            <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-card-title
            v-if="!hideHeader && !immersive"
            class="modal-content-title flex-shrink-0 mb-0"
            :class="(flatChrome || stitch) ? 'modal-content-title--flat' : 'primary-ultralight-grey'"
        >
            <span class="modal-content-title-inner text-h3 pl-1 text-truncate">
                <slot name="title"> </slot>
                <v-icon
                    v-if="helpContentId && uniqueIdExists"
                    class="ml-4"
                    left
                    @click="openHelp"
                    >help_outline</v-icon
                >
            </span>
        </v-card-title>

        <v-card-text
            class="modal-content-body pa-0 min-height-0"
            :class="bodyClasses"
        >
            <div v-if="stitch && $slots.rawcontent" class="stitch-modal stitch-modal--split stitch-form general-settings-reference modal-stitch-raw">
                <slot name="rawcontent" />
            </div>
            <slot v-else name="rawcontent" class="w-100">
                <div
                    class="modal-content-body-inner px-4 px-sm-6 pt-4 pb-8"
                    :class="
                        fillHeight
                            ? 'w-100 h-100 min-height-0 d-flex flex-column flex-grow-1'
                            : 'w-100'
                    "
                >
                    <div v-if="stitch" class="stitch-modal stitch-form general-settings-reference modal-stitch-shell">
                        <div class="stitch-modal__card modal-stitch-surface">
                            <slot name="content" />
                        </div>
                    </div>
                    <slot v-else name="content" class="w-100"> </slot>
                </div>
            </slot>
        </v-card-text>
        <div
            v-if="!hideFooter && !immersive"
            class="modal-content-bottom flex-shrink-0 py-0 d-sm-flex d-block justify-end align-center"
            :class="
                (flatChrome || stitch)
                    ? 'modal-content-bottom--flat px-6 px-sm-8'
                    : 'primary-ultralight-grey px-2 px-sm-6'
            "
        >
            <slot name="bottom"> </slot>
        </div>
    </div>
</template>

<script>
import { mapState } from "vuex";
import GlobalMixins from "@/mixins/global";
import Help from "@/modals/help.vue";

export default {
    mixins: [GlobalMixins],
    i18n: { messages: { fr: { immersiveClose: "Fermer" }, en: { immersiveClose: "Close" } } },
    props: {
        /** Shared Stitch shell for forms without their own section canvas. */
        stitch: { type: Boolean, default: false },
        immersive: { type: Boolean, default: false },
        closeDisabled: { type: Boolean, default: false },
        hideHeader: { type: Boolean, default: false },
        hideFooter: { type: Boolean, default: false },
        helpContentId: {
            type: String,
            required: false,
        },
        /** Header/footer styling aligned with enterprise-style modals (plain header bar + muted footer). */
        flatChrome: {
            type: Boolean,
            default: false,
        },
        /** When true, body expands up to viewport and scrolls (large forms). Default: size to content. */
        fillHeight: {
            type: Boolean,
            default: false,
        },
        /** Keep footer pinned while the body scrolls (enterprise split modals). */
        pinnedFooter: {
            type: Boolean,
            default: false,
        },
    },
    computed: Object.assign(mapState("help", ["uniqueIds"]), {
        bodyClasses() {
            if (this.fillHeight) {
                return "modal-content-body--fill d-flex flex-column flex-grow-1 flex-shrink-1 overflow-hidden";
            }
            return "modal-content-body--scroll modal-content-body--pinned flex-grow-1 flex-shrink-1 min-height-0";
        },
        uniqueIdExists() {
            if (!this.uniqueIds || !this.helpContentId) {
                return false;
            }
            return this.uniqueIds.includes(this.helpContentId);
        },
    }),
    methods: {
        openHelp() {
            this.showModal({
                component: Help,
                params: {
                    contentUniqueId: this.helpContentId,
                },
            });
        },
    },
};
</script>

<style lang="scss" scoped>
@import "@/assets/css/mixins.scss";
@import "@/assets/css/_stitch-form.scss";

.modal-stitch-shell { min-width: 0; }
.modal-stitch-surface { padding: 20px; }
.modal-stitch-raw { min-height: 0; flex: 1 1 auto; }
.modal-stitch-raw > ::v-deep * { min-width: 0; }
.modal-stitch-shell, .modal-stitch-raw {
    ::v-deep .stitch-modal__title { @include stitch-heading; }
    ::v-deep .stitch-modal__fields { margin: -8px; }
    ::v-deep .stitch-modal__fields > .col,
    ::v-deep .stitch-modal__fields > [class*="col-"] { padding: 8px; }
}
.modal-content-root--stitch {
    .modal-content-body { background: #eef2f7; }
    .modal-content-bottom { padding-top: 12px !important; padding-bottom: 12px !important; gap: 12px; flex-wrap: wrap; }
    .modal-content-bottom ::v-deep .v-btn { margin-top: 0 !important; margin-bottom: 0 !important; text-transform: none; letter-spacing: normal; }
    .modal-content-title-inner { white-space: normal !important; word-break: normal; overflow-wrap: break-word; }
}
@media (max-width: 599px) {
    .modal-stitch-shell { margin-left: -12px; margin-right: -12px; padding-left: 12px; padding-right: 12px; }
    .modal-stitch-surface { padding: 16px; }
}

.modal-content-root--immersive { position: relative; }
.modal-immersive-close { position: absolute; top: 12px; right: 12px; z-index: 1; }

.modal-content-root {
    flex: 1 1 auto;
    min-height: 0;
    max-height: 100%;
}

.modal-content-body--pinned {
    overflow-x: hidden;
    overflow-y: auto;
    max-height: none !important;
    -webkit-overflow-scrolling: touch;
}

::v-deep .modal-content-body--pinned.v-card__text {
    flex: 1 1 auto !important;
    min-height: 0;
    height: auto;
}

.modal-content-root--fill {
    display: flex;
    flex-direction: column;
    max-height: 100%;
    height: 100%;
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
}

.modal-content-body--fill {
    flex: 1 1 auto;
    min-height: 0;
    max-height: none;
    overflow: hidden;
}

.modal-content-body--fill > :first-child {
    display: flex;
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
}

::v-deep .modal-content-body--fill.v-card__text {
    flex: 1 1 auto !important;
    min-height: 0;
}

.modal-content-body--scroll {
    max-height: none;
}

.modal-content-body {
    background: inherit;
}

.modal-content-root--flat-chrome .modal-content-title--flat {
    background: #ffffff !important;
    border-bottom: 1px solid #f3f4f6;
    padding: 16px 24px !important;
}

.modal-content-root--flat-chrome .modal-content-title-inner {
    font-size: 1.25rem !important;
    line-height: 1.75rem;
    font-weight: 600 !important;
    color: #1f2937 !important;
}

.modal-content-root--flat-chrome .modal-content-bottom--flat {
    background: #f9fafb !important;
    border-top: 1px solid #f3f4f6;
}

@include respond-to(xs) {
    /* The dialog is edge to edge on phones: keep the chrome paddings tight so
       nested cards and inputs keep as much width as possible. */
    .modal-content-title {
        padding-left: 12px;
        padding-right: 12px;
    }

    .modal-content-root--flat-chrome .modal-content-title--flat {
        padding-left: 12px !important;
        padding-right: 12px !important;
    }

    .modal-content-body-inner {
        padding-left: 12px !important;
        padding-right: 12px !important;
    }

    .modal-content-bottom {
        display: flex !important;
        flex-direction: column;
        align-items: stretch;
        gap: 8px;
        /* The gap is the single source of spacing: neutralize the buttons'
           own my-* margins (which stacked unevenly with the gap) and give
           the footer its vertical padding back (the element carries py-0). */
        padding-top: 12px !important;
        padding-bottom: 12px !important;

        > * {
            width: 100%;
            margin: 0 !important;
        }

        /* A v-spacer between buttons adds an empty flex child, doubling the
           gap around it: irrelevant in column layout. */
        > .spacer {
            display: none;
        }

        ::v-deep .v-btn,
        ::v-deep button {
            width: 100%;
            max-width: 100%;
        }
    }
}
</style>
